import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { scopedCollection } from '../server/scoped-collection.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { gqlEnumValues } from '@adornis/bh-util/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { BHPayment } from '../entities/BHPayment.js';
import { Participation } from '../entities/Participation.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';

@Entity()
export class ParticipationDataviewStateStat extends AdornisEntity {
  static override _class = 'ParticipationDataviewStateStat';

  public static get neededGqlFields(): BaseQLSelectionSet<ParticipationDataviewStateStat> {
    return { key: 1, ammount: 1 };
  }

  @validate(nonOptional())
  @Field(type => String)
  key!: string;

  @validate(nonOptional())
  @Field(type => Int)
  ammount!: number;
}

export type ParticipationStateStatsMappedEnitity = { [k in ParticipationState]: ParticipationDataviewStateStat };
export type ParticipationStateStatsMappedNumber = { [k in ParticipationState]: number };

export const getEmptyParticipationStateStats = () =>
  Object.fromEntries(
    gqlEnumValues(ParticipationState).map(key => [key, new ParticipationDataviewStateStat({ key: key, ammount: 0 })]),
  ) as ParticipationStateStatsMappedEnitity;

@Entity()
export class ParticipationDataview extends AdornisEntity {
  static override _class = 'ParticipationDataview';

  static override resolveID(entity: ParticipationDataview): string {
    return entity.participation._id;
  }

  @validate(nonOptional())
  @Field(type => String)
  _id!: string;

  @validate(nonOptional())
  @Field(type => Participation)
  participation!: Participation;

  @Field(type => String) productID: Maybe<string>;

  @Field(type => BHPayment, {
    resolve: function (this: ParticipationDataview) {
      return async () => {
        const c = await scopedCollection<BHPayment>(BHPayment._class);
        const payment = (await c.findOne({ participationIDs: this.participation._id })) as Maybe<BHPayment>;
        return payment;
      };
    },
  })
  payment: Maybe<BHPayment>;

  @validate(nonOptional())
  @Field(type => User)
  buyer!: User;

  @Field(type => User)
  berater?: User;
}
